import { version } from 'react';
import type { InteractionMetrics } from '@atlaskit/react-ufo/interaction-metrics';
import getMeta from '@atlassian/jira-get-meta';
import { getProductEditions } from '@atlassian/jira-get-meta/src/utils/get-meta-product-editions/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getAssetEncoding } from './utils/asset-encoding/index.tsx';
import { getCacheHitRatio } from './utils/cache-hit-ratio/index.tsx';
import { getCHRInfo } from './utils/chr/index.tsx';
import { getLighthouseMetrics } from './utils/lighthouse-metrics/index.tsx';
import { getMarks } from './utils/marks/index.tsx';
import { getMemoryUsageReport } from './utils/memory-usage/index.tsx';
import {
	getPageSizeMetrics,
	getExtraNavigationTimings,
	getPageSizeInfo,
	getPopInfo,
} from './utils/page-size/index.tsx';
import { getRouterResourceMetrics } from './utils/router-resource-metrics/index.tsx';
import { getServerDurationMetrics } from './utils/server-duration/index.tsx';
import { getSSRStateInformation } from './utils/ssr/ssr.tsx';
import { getStickyVersionsState } from './utils/sticky-versions/state.tsx';
import { getWRMStats } from './utils/wrm-stats/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { startLighthouseObserver, getLighthouseMetrics } from './utils/lighthouse-metrics';

export function getJiraCustomData(interaction: InteractionMetrics) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let customData: Record<string, any> = {};
	const shard = getMeta('ajs-shard');
	if (shard !== null && shard !== '') {
		customData.shard = shard;
	}

	if (fg('add_editions_to_bm3_splunk_events')) {
		customData['jira:productEditions'] = getProductEditions();
	}

	customData['network:assetEncoding'] = getAssetEncoding();

	if (fg('jira-enable-sticky-versions-state')) {
		customData['stickyVersions:state'] = getStickyVersionsState();
	}

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const buildVariant = window.BUILD_VARIANT;
	if (buildVariant) {
		customData['app:buildVariant'] = buildVariant;
	}

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const bundlerVersion = window.BUNDLER_VERSION;
	if (bundlerVersion) {
		customData['app:bundlerVersion'] = bundlerVersion;
	}

	customData.httpProtocol =
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		performance
			.getEntriesByType('resource')
			// @ts-expect-error - TS2339  - Property 'nextHopProtocol' does not exist on type 'PerformanceEntry'. | TS2339 - Property 'nextHopProtocol' does not exist on type 'PerformanceEntry'.
			.find(({ nextHopProtocol }) => !!nextHopProtocol)?.nextHopProtocol ?? 'unknown';

	const { start, end, apdex } = interaction;

	const stop = apdex?.[0]?.stopTime ?? end;

	customData.memory = getMemoryUsageReport({ start, stop });

	const lightHouseMetrics = getLighthouseMetrics({
		start,
		stop,
	});

	customData = {
		...customData,
		...lightHouseMetrics,
	};

	if (interaction.type === 'page_load') {
		customData = {
			...customData,
			...getMarks({ start }),
			...getPageSizeMetrics(),
			...getExtraNavigationTimings(),
			...getPopInfo(),
			...getServerDurationMetrics(),
			...getRouterResourceMetrics(),
			...getWRMStats(),
			...getPageSizeInfo({ start, stop }),
			...getCacheHitRatio({ start }),
			...getCHRInfo({ start }),
			...getSSRStateInformation(),

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			'app:version:web': window.BUILD_KEY,

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			'app:version:ssr': window.SSR_BUILD_KEY,
			'app:framework:name': 'react',
			'app:framework:version': version,
		};
	}
	return customData;
}
